<script setup lang='ts'>
import { computed, ref } from 'vue';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { FormBuilderContract } from '@/components/builder/form';
import { $t } from '@/plugins/localization';
import { useAlerts } from '@/plugins/alerts';
import StorageService, { Resource as FileResource } from '@/modules/core/files/services/StorageService';
import BpmnService from '@/modules/low-code/process/services/BpmnService';
import { saveAs } from 'file-saver';
import properties from '../../properties';
import { FormEntry } from '../form';
import { DownloadFileItem, LogitoDownloadFileType } from '.';

const MAX_FILES = 10;
const MAX_FILE_SIZE_MB = 50;

defineOptions({
    name: 'logito-download-file-blueprint',
    components: {
        ...properties,
    },
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const { $alert } = useAlerts();

const blueprint = computed(() => props.blueprint);
const design = computed(() => props.form.designMode());

const files = ref<FileResource[]>([]);

const uploadFiles = async () =>
{
    const items = files.value ?? [];

    for (const item of items)
    {
        if (item.upload && !item.publicId)
        {
            try
            {
                const response = await StorageService.upload(item.upload, 'users', true, false, false, 'public');

                item.publicId = response.publicId;
                item.name = response.name ?? item.name;
                item.url = response.url;
            }
            catch (ex)
            {
                $alert.error($t('[[[Nie udało się wgrać pliku: %0|||{0}]]]', item.name));
            }
        }
    }

    files.value = items.filter(p => !!p.publicId);
    blueprint.value.files = files.value.map(p => ({ fileId: p.publicId, fileName: p.name }));
};

const initFiles = async () =>
{
    const result: FileResource[] = [];

    for (const item of blueprint.value.files ?? [])
    {
        try
        {
            const response = await StorageService.fetchResourceSimple(item.fileId);

            if (response.result)
            {
                result.push(response.result);
            }
        }
        catch (ex)
        {
            // plik mógł zostać usunięty ze storage — pomijamy wpis
        }
    }

    files.value = result;
};

const download = async (item: DownloadFileItem) =>
{
    if (design.value)
        return;

    try
    {
        const response = await BpmnService.getDownloadFileContent(props.entry.module, props.entry.formId, blueprint.value.name, item.fileId);

        const blob = new Blob([response], {
            type: response.type,
        });

        saveAs(blob, item.fileName || $t('[[[Plik do pobrania]]]'));
    }
    catch (ex)
    {
        $alert.error($t('[[[Wystąpił problem podczas pobierania pliku]]]'));
    }
};

initFiles();
</script>

<template>
    <form-component-wrapper :form="form" :parent="parent" :blueprint="blueprint" :table-row-index="props.index">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" />
                <div v-if="blueprint.files && blueprint.files.length > 0" class="d-flex flex-column align-items-start gap-2">
                    <ideo-button
                        v-for="item in blueprint.files"
                        :key="item.fileId"
                        variant="secondary"
                        icon="fa-solid fa-file-arrow-down"
                        :disabled="design"
                        @click="download(item)"
                    >
                        {{ item.fileName || $t('[[[Pobierz plik]]]') }}
                    </ideo-button>
                </div>
                <span v-else class="text-muted">{{ $t('[[[Nie wgrano pliku]]]') }}</span>
                <form-help :form="form" :blueprint="blueprint" />
            </div>
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <ideo-form-localize v-slot="{ locale }">
                <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
            </ideo-form-localize>
            <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
            <ideo-form-localize v-slot="{ locale }">
                <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
            </ideo-form-localize>
            <ideo-form-group :label="$t('[[[Plik do pobrania]]]')" :invalid-feedback="form.schema.errorMessage(blueprint, 'files')" required>
                <fileupload :max-files="MAX_FILES" :max-size="MAX_FILE_SIZE_MB" v-model="files" @update:modelValue="uploadFiles" />
            </ideo-form-group>
            <field-visible :form="form" :blueprint="blueprint" :label="$t('[[[Widoczność]]]')" />
        </template>
    </form-component-wrapper>
</template>
